import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import type { CargoConContexto } from './api';
import { Button } from '@/components/ui/button';
import { formatClp } from '@/lib/format/currency';
import { formatLongDate, formatPeriodo } from '@/lib/format/date';
import { cn } from '@/lib/utils/cn';
import { useCondoContext } from '@/features/auth/use-condo-context';

interface Props {
  cargos: CargoConContexto[];
}

export function MisCargosList({ cargos }: Props) {
  const navigate = useNavigate();
  const { condoId } = useCondoContext();
  const pendientes = cargos.filter((c) => c.estado !== 'pagado');
  const pagados = cargos.filter((c) => c.estado === 'pagado');
  const totalPendiente = pendientes.reduce(
    (s, c) => s + (c.montoBase - c.montoPagado),
    0,
  );

  return (
    <div className="space-y-10">
      {/* Balance hero */}
      <section className="relative overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-primary)] p-8 text-[var(--color-paper)]">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full border border-[color:color-mix(in_oklch,var(--color-paper)_12%,transparent)] opacity-40"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full border border-[color:color-mix(in_oklch,var(--color-paper)_10%,transparent)] opacity-25"
        />
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] opacity-85">
          Saldo pendiente
        </p>
        <p className="font-display-tight mt-3 text-6xl font-semibold tabular tracking-tight">
          {formatClp(totalPendiente)}
        </p>
        <p className="mt-3 max-w-md text-sm opacity-85">
          {pendientes.length === 0
            ? 'Estas al dia. Todos tus cargos del periodo estan pagados.'
            : `Tienes ${pendientes.length} cargo(s) por pagar.`}
        </p>
      </section>

      {/* Pending */}
      <section>
        <header className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-semibold">Cargos pendientes</h2>
          <span className="eyebrow">{pendientes.length}</span>
        </header>
        {pendientes.length === 0 ? (
          <p className="text-sm text-[var(--color-ink-muted)]">
            No tienes cargos pendientes.
          </p>
        ) : (
          <ul className="space-y-3">
            {pendientes.map((c) => (
              <li
                key={c.id}
                className="group relative overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] transition-all duration-150 hover:border-[var(--color-accent)]"
              >
                <div
                  aria-hidden
                  className={cn(
                    'absolute left-0 top-0 h-full w-[3px]',
                    c.estado === 'vencido'
                      ? 'bg-[var(--color-danger)]'
                      : 'bg-[var(--color-accent)]',
                  )}
                />
                <div className="flex flex-col items-start justify-between gap-4 p-5 sm:flex-row sm:items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <StatusIcon estado={c.estado} />
                      <p className="font-display text-lg font-semibold">
                        {c.gastoComun
                          ? formatPeriodo(c.gastoComun.periodo)
                          : 'Cargo'}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
                      Unidad {c.unidad?.numero ?? '—'} · Vence{' '}
                      {formatLongDate(c.fechaVencimiento)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-display-tight text-2xl font-semibold tabular">
                      {formatClp(c.montoBase - c.montoPagado)}
                    </p>
                    <Button
                      variant="accent"
                      onClick={() => navigate(`/app/condominios/${condoId}/pagos/iniciar/${c.id}`)}
                    >
                      Pagar
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* History */}
      {pagados.length > 0 && (
        <section>
          <header className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-2xl font-semibold">Historial</h2>
            <span className="eyebrow">{pagados.length} cargos</span>
          </header>
          <ul className="divide-y divide-[var(--color-hairline)] rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)]">
            {pagados.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-4 px-5 py-3"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 text-[var(--color-moss)]" />
                  <div>
                    <p className="font-display text-sm font-semibold">
                      {c.gastoComun ? formatPeriodo(c.gastoComun.periodo) : 'Cargo'}
                    </p>
                    <p className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                      Unidad {c.unidad?.numero ?? '—'}
                    </p>
                  </div>
                </div>
                <p className="font-display tabular text-sm text-[var(--color-ink-muted)] line-through">
                  {formatClp(c.montoBase)}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function StatusIcon({ estado }: { estado: CargoConContexto['estado'] }) {
  if (estado === 'vencido') {
    return <AlertCircle className="h-4 w-4 text-[var(--color-danger)]" />;
  }
  return <Clock className="h-4 w-4 text-[var(--color-accent)]" />;
}
